// paystack-initialize's refusals, as the widget keeps them and says them.
//
// The function answers most refusals with a non-2xx status: 400/404/409 for a
// request it cannot use, 422 tenant_not_connected / platform_not_configured,
// 500 when it could not prepare the payment safely, 502
// card_payments_unavailable (Paystack refused the key, #1578),
// subaccount_invalid, paystack_unreachable (main) or Paystack's own text. Only
// the second-charge guard's refusals, initialization_in_progress and
// payment_superseded come back as HTTP 200 ok:false. supabase-js keeps nothing
// of a non-2xx body, so the guest read "Couldn't start checkout: Edge Function
// returned a non-2xx status code" for every one of them.
// initializePaystackCheckout (state/booking.ts) hands what functions.invoke
// answered to initAnswerRefusal, which reads the body (edge-error.ts), and
// describeInitError words the refusal. Pure: no client, so
// e2e/paystack-init-error-logic.spec.ts runs this very code on what the real
// supabase-js client hands back. The same file on main and on staging.
import { invokeErrorBody } from "./edge-error";

/** A refusal: its code, and the server's own sentence and the earlier payment it is about, when sent. */
export type PaystackInitRefusal = {
  ok: false;
  error: string;
  message?: string;
  earlier_reference?: string;
};

/**
 * The widget's own code for a non-2xx answer with no code in it to read: the
 * request never got an answer (FunctionsFetchError), the function crashed (the
 * runtime's own text 500), a gateway's HTML page, a relay error. The server
 * never sends it.
 */
const PAYMENT_SERVICE_UNAVAILABLE = "payment_service_unavailable";

/**
 * paystack-initialize's answer without a page, as initializePaystackCheckout
 * hands it back: the code (init_failed when there is none), and the server's
 * own sentence and the earlier payment's reference when it sent them (the
 * second-charge guard's refusals, 20260928090040; the key refusal,
 * 20261005120010). One shape for a 2xx ok:false and a non-2xx body alike, so
 * the two can never drift.
 */
export function initRefusal(
  body: { error?: unknown; message?: unknown; earlier_reference?: unknown } | null | undefined,
): PaystackInitRefusal {
  return {
    ok: false,
    error: typeof body?.error === "string" ? body.error : "init_failed",
    ...(typeof body?.message === "string" && body.message ? { message: body.message } : {}),
    ...(typeof body?.earlier_reference === "string" && body.earlier_reference
      ? { earlier_reference: body.earlier_reference }
      : {}),
  };
}

/**
 * Why paystack-initialize started no payment, from what functions.invoke
 * handed back (`data`, `error`); null when it answered with a page. The error
 * first: a non-2xx answer, whose code is in its body (with none to read,
 * payment_service_unavailable, never supabase-js's sentence). Then a 2xx
 * ok:false.
 */
export async function initAnswerRefusal(data: unknown, error: unknown): Promise<PaystackInitRefusal | null> {
  if (error) {
    const body = await invokeErrorBody(error);
    if (typeof body?.error === "string" && body.error) return initRefusal(body);
    return { ok: false, error: PAYMENT_SERVICE_UNAVAILABLE };
  }
  const answer = data as
    | { ok?: unknown; error?: unknown; message?: unknown; earlier_reference?: unknown }
    | null
    | undefined;
  return answer?.ok ? null : initRefusal(answer);
}

/** Paystack didn't answer, or answered a bare 5xx: /finish's sentence (reasonMsg("paystack_unreachable")). */
const PROVIDER_DOWN = "Our payment provider didn't respond. Nothing was charged — please try again in a minute.";
/** Paystack's own text when its answer had no message of its own ("paystack 503"). */
const PAYSTACK_5XX = /^paystack 5\d\d$/;
/** A code, said as it came; anything else from the server is Paystack's own text. */
const CODE = /^[a-z0-9_]+$/;

// The second-charge guard's refusals (20260928090040) are notices, not
// errors: see state/charge-guard.ts and refuseInit in checkout/useCheckoutSubmit.
// Everything else is said here; `message` is the
// server's own sentence, when it sent one.
export function describeInitError(err: string, message?: string | null): string {
  const said = typeof message === "string" ? message.trim() : "";
  switch (err) {
    // No Paystack connection (Fijnland on staging), Vertu's own Paystack
    // account not set up, or Paystack refused the venue's payout account.
    // Trying again won't help: the hosted pages' sentence.
    case "tenant_not_connected":
    case "platform_not_configured":
    case "subaccount_invalid":
      return "Online payments are not available for this venue right now. Please contact the venue directly.";
    // Paystack refused the key, not the card (paystack-initialize,
    // 20261005120010, #1578): the server's sentence, else this copy of it
    // (paystack-key-alert.test.ts keeps the two identical).
    case "card_payments_unavailable":
      return said || "Card payments are unavailable right now. You have not been charged. Please try again a little later.";
    // The server could not prepare the payment safely (the venue's routing,
    // its claim on the payment, the account record, the stored page, the
    // operator split's sums, 20261006090010), so nothing was started: the
    // /deposit page's sentence.
    case "routing_lookup_failed":
    case "initialization_claim_failed":
    case "account_record_failed":
    case "authorization_store_failed":
    case "split_formula_mismatch":
      return "We couldn't securely prepare the payment right now. Please wait a moment and try again.";
    // Another press is preparing this payment's page (HTTP 200): the hosted
    // pages' sentence.
    case "initialization_in_progress":
      return "Another payment window is being prepared. Wait a moment, then try again.";
    // Paystack didn't answer, even after the gateway retry (main's 502).
    case "paystack_unreachable":
      return PROVIDER_DOWN;
    // No code to read (initAnswerRefusal): nothing says how far the request
    // got, so never "nothing was charged" here.
    case PAYMENT_SERVICE_UNAVAILABLE:
      return "We couldn't start the payment. Please try again in a minute.";
    // The booking changed while the payment page was being made
    // (paystack-initialize, 20261003200010): a new press prices it as it stands.
    case "payment_superseded":
      return "Your booking changed a moment ago. Please press Pay again to pay the updated total.";
  }
  if (PAYSTACK_5XX.test(err)) return PROVIDER_DOWN;
  if (said) return said;
  // A code is shown as it came. Paystack's own text never is: "Duplicate
  // Transaction Reference" reads like a double charge, and a split refusal can
  // name an operator's subaccount. Only the email is the guest's to fix.
  if (CODE.test(err)) return `Couldn't start checkout: ${err}`;
  if (/\bemail\b/i.test(err)) return "Paystack didn't accept that email address. Please check it and try again.";
  return "We couldn't start the payment. Please try again, or contact the venue.";
}
